import { Box, Button } from '@mui/material';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { INode, IPage, ITypeNode } from '../../NewFormBuilder/Context/FormBuilderContext';
import { getNodesForPage } from '../../NewFormBuilder/utils/pageUtils';
import { usePublishedForm } from '../Context/PublishedFormContext';
import { Image } from '../Nodes/Image';
import { Input } from '../Nodes/Input';
import { List } from '../Nodes/List';
import { Output } from '../Nodes/Output';
import { ShortText } from '../Nodes/ShortText';
import { Submit } from '../Nodes/Submit';
import { Title } from '../Nodes/Title';

const ResponsiveReactGridLayout = WidthProvider(Responsive) as any;

interface SwapPageViewProps {
  publishedNodes: INode[];
  pages: IPage[];
  isSwapPage?: boolean;
}

interface PageContentProps {
  nodes: INode[];
}

function PageContent({ nodes }: PageContentProps) {
  const breakpoints = useMemo(
    () => ({
      lg: 1200,
      md: 996,
      sm: 768,
      xs: 480,
      xxs: 0,
    }),
    [],
  );

  const cols = useMemo(
    () => ({
      lg: 12,
      md: 10,
      sm: 6,
      xs: 4,
      xxs: 2,
    }),
    [],
  );

  const generateResponsiveLayouts = useMemo(() => {
    if (!nodes || nodes.length === 0) {
      return { lg: [], md: [], sm: [], xs: [], xxs: [] };
    }

    const layouts: { [key: string]: any[] } = {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: [],
    };

    const sortedNodes = [...nodes].sort((a, b) => {
      const yA = a.layout.y;
      const yB = b.layout.y;
      if (yA !== yB) {
        return yA - yB;
      }
      return a.layout.x - b.layout.x;
    });

    Object.keys(cols).forEach(breakpoint => {
      const numCols = cols[breakpoint as keyof typeof cols];
      const lgCols = cols.lg;

      const isMobile = breakpoint === 'xs' || breakpoint === 'xxs';

      layouts[breakpoint] = sortedNodes.map((node, index) => {
        const lgLayout = node.layout;

        if (isMobile) {
          let cumulativeY = 0;
          for (let i = 0; i < index; i++) {
            const prevNode = sortedNodes[i];
            cumulativeY += prevNode.layout.h;
          }

          return {
            ...lgLayout,
            i: node.id,
            x: 0,
            y: cumulativeY,
            w: numCols,
          };
        }

        const ratio = numCols / lgCols;
        const newX = Math.floor(lgLayout.x * ratio);
        const newW = Math.max(1, Math.floor(lgLayout.w * ratio));

        return {
          ...lgLayout,
          i: node.id,
          x: Math.min(newX, numCols - newW),
          w: newW,
        };
      });
    });

    return layouts;
  }, [nodes, cols]);

  return (
    <Box
      sx={{
        width: {
          xs: '100%',
          md: '1300px',
        },
        flexShrink: 0,
        height: '100%',
        maxHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: {
          xs: 0,
          md: 0,
        },
        margin: {
          xs: 0,
          md: 0,
        },
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      <Box
        sx={{
          '& .react-grid-layout': {
            paddingLeft: {
              xs: 0,
              md: '10px',
            },
            paddingRight: {
              xs: 0,
              md: '10px',
            },
          },
        }}
      >
        <ResponsiveReactGridLayout
          className="layout"
          layouts={generateResponsiveLayouts}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={30}
          isDraggable={false}
          isResizable={false}
          verticalCompact={false}
          margin={[10, 10]}
          containerPadding={[10, 10]}
        >
          {nodes.map(node => (
            <div
              key={node.id}
              style={{
                border: `${node.data.styles.borderWeight}px solid ${node.data.styles.borderColor}`,
                borderRadius: node.data.styles.borderRadius,
                backgroundColor: node.data.styles.backgroundColor,
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  padding: `${
                    node.data.styles.padding !== undefined &&
                    node.data.styles.padding !== null
                      ? node.data.styles.padding
                      : 16
                  }px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: node.data.styles.verticalAlign,
                }}
              >
                {node.type === ITypeNode.TITLE && <Title node={node} />}

                {node.type === ITypeNode.SHORT_TEXT && (
                  <ShortText node={node} />
                )}

                {node.type === ITypeNode.IMAGE && <Image node={node} />}

                {node.type === ITypeNode.LIST && <List node={node} />}

                {node.type === ITypeNode.INPUT && <Input node={node} />}

                {node.type === ITypeNode.OUTPUT && <Output node={node} />}

                {node.type === ITypeNode.SUBMIT && <Submit node={node} />}
              </div>
            </div>
          ))}
        </ResponsiveReactGridLayout>
      </Box>
    </Box>
  );
}

export function SwapPageView({ publishedNodes, pages, isSwapPage = false }: SwapPageViewProps) {
  const { redirectPageIdAfterSubmit, clearRedirectPageId, isSubmitting } = usePublishedForm();
  const previousSubmittingRef = useRef(isSubmitting);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const pageNodes = useMemo(() => {
    if (!isSwapPage) {
      return [{ page: null as any, nodes: publishedNodes || [] }];
    }

    if (!pages || pages.length === 0) {
      return [];
    }

    return pages.map(page => ({
      page,
      nodes: getNodesForPage(page, publishedNodes || []),
    }));
  }, [isSwapPage, pages, publishedNodes]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      align: 'start',
      slidesToScroll: 1,
      containScroll: 'trimSnaps',
      watchSlides: true,
      dragFree: false,
    },
    []
  );

  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollButtons();
    emblaApi.on('select', updateScrollButtons);
    emblaApi.on('reInit', updateScrollButtons);

    return () => {
      emblaApi.off('select', updateScrollButtons);
      emblaApi.off('reInit', updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    const submitJustFinished = previousSubmittingRef.current && !isSubmitting;
    previousSubmittingRef.current = isSubmitting;

    if (submitJustFinished && redirectPageIdAfterSubmit && emblaApi && pageNodes.length > 0 && isSwapPage) {
      const targetPageIndex = pageNodes.findIndex(
        ({ page }) => page && page.id === redirectPageIdAfterSubmit
      );

      if (targetPageIndex !== -1) {
        emblaApi.scrollTo(targetPageIndex, false);
        clearRedirectPageId();
      } else {
        clearRedirectPageId();
      }
    }
  }, [isSubmitting, redirectPageIdAfterSubmit, emblaApi, pageNodes, clearRedirectPageId, isSwapPage]);


  if (pageNodes.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        className="embla"
        sx={{
          width: {
            xs: '100%',
            md: '1300px',
          },
          overflowX: 'hidden',
          overflowY: 'hidden',
          position: 'relative',
          height: '100vh',
          '& .embla__viewport': {
            overflow: 'hidden',
            width: '100%',
            height: '100%',
          },
          '& .embla__container': {
            display: 'flex',
            touchAction: 'pan-x pan-y pinch-zoom',
            alignItems: 'stretch',
            height: '100%',
          },
          '& .embla__slide': {
            flex: '0 0 100%',
            minWidth: 0,
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <div className="embla__viewport" ref={emblaRef}>
          <Box className="embla__container">
            {pageNodes.map(({ page, nodes }) => (
              <Box key={page?.id || 'single-page'} className="embla__slide">
                <PageContent nodes={nodes} />
              </Box>
            ))}
          </Box>
        </div>
      </Box>

      {isSwapPage && pages && pages.length > 1 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: 'flex',
            gap: 1,
            zIndex: 1000,
            backgroundColor: 'white',
            borderRadius: 2,
            border: '1px solid #EAECF0',
            padding: '12px',
          }}
        >
          <Button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            sx={{
              color: canScrollPrev ? '#101828' : '#667085',
              '&:hover': {
                backgroundColor: canScrollPrev
                  ? 'action.hover'
                  : 'transparent',
              },
              borderRadius: '8px',
              border: '1px solid #EAECF0',
              fontSize: '14px',
            }}
            startIcon={<CaretLeft size={18} />}
          >
            Previous
          </Button>
          <Button
            onClick={scrollNext}
            disabled={!canScrollNext}
            sx={{
              color: canScrollNext ? '#101828' : '#667085',
              '&:hover': {
                backgroundColor: canScrollNext ? 'action.hover' : 'transparent',
              },
              borderRadius: '8px',
              border: '1px solid #EAECF0',
              fontSize: '14px',
            }}
            endIcon={<CaretRight size={18} />}
          >
            Next
          </Button>
        </Box>
      )}
    </>
  );
}

